import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { trigger, transition, useAnimation } from '@angular/animations';
import { fadeIn } from 'ng-animate';
import { CcssService } from '@services/ccss/ccss.service';
import { GuidedSearchService } from '@services/guided-search/guided-search.service';
import { ProceduresService } from '@services/procedures.service';
import { Subscription, forkJoin, Observable } from 'rxjs';
import { first, filter } from 'rxjs/operators';
import { TermAutosuggest } from '@classes/term-autosuggest.class';
import { Breakpoints } from '@classes/breakpoints.class';
import { SettingsService } from '@services/settings.service';
import { SubscriptionManager } from '@zelis/platform-ui-components';
import * as _ from 'lodash';
import { PromotionalSmartshopperSettings } from '@interfaces/promotional-smartshopper-settings.interface';
import { TrackByUtilities } from '@utilities/trackByFn.utilities';
import { TranslateService } from '@ngx-translate/core';
import { IncentivizedProcedure } from '@interfaces/incentivized-procedure.model';
import { Store } from '@ngrx/store';
import { setSearchMethodAction } from '@store/search/search.actions';
import { SearchMethods } from '@enums/search-methods.enum';
import { SearchState } from '@store/search/search.state';

@Component({
  selector: 'app-promotional-smartshopper',
  templateUrl: './promotional-smartshopper.component.html',
  styleUrls: ['./promotional-smartshopper.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(
        '* => *',
        useAnimation(fadeIn, {
          params: { timing: 1.1 },
        })
      ),
    ]),
  ],
})
export class PromotionalSmartshopperComponent implements OnInit, OnDestroy {
  public incentivizedProceduresCount: number;
  public procedures: IncentivizedProcedure[];
  public subscriptions = new SubscriptionManager();
  public ssData: boolean = false;
  public earnIconSrc: string = `/assets/images/rts-earn-icon-primary.svg`;
  public scheduleIconSrc: string = `/assets/images/rts-schedule-icon-primary.svg`;
  public trackByFnItemId: any = TrackByUtilities.trackByFnItemId;
  private maxPromotedProcedures: number = 5;

  constructor(
    public ccss: CcssService,
    public breakpoints: Breakpoints,
    private guidedSearchService: GuidedSearchService,
    private proceduresService: ProceduresService,
    private termAutosuggest: TermAutosuggest,
    private settingsService: SettingsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private translateService: TranslateService,
    private store: Store<SearchState>
  ) {}

  ngOnInit() {
    this.setPromotedProcedures();
    this.subscribeToTranslationChange();
    this.setEarnIcon();
    this.setScheduleIcon();
  }

  ngOnDestroy() {
    this.subscriptions.destroy();
  }

  public openGuidedSearchProcedures(): void {
    this.store.dispatch(
      setSearchMethodAction({
        searchMethod: SearchMethods.SmartShopperHomePageViewAll,
      })
    );
    this.guidedSearchService.openGuidedSearchByType('incentivized_procedures');
  }

  public procedureSearch(procedureId: number): void {
    this.store.dispatch(
      setSearchMethodAction({
        searchMethod: SearchMethods.SmartShopperHomePage,
      })
    );
    this.ngZone.run(() => {
      this.termAutosuggest.activateSearch({
        type: 'procedures',
        id: procedureId,
      });
    });
  }

  private setPromotedProcedures(): Subscription {
    return forkJoin([
      this.proceduresService.requestIncentivizedProcedures().pipe(first()),
      this.getPromotionalSsSettings(),
    ]).subscribe(([incentivizedProcedures, config]) => {
      if (incentivizedProcedures && incentivizedProcedures.length > 0) {
        this.setProcedureData(config, incentivizedProcedures);
        this.procedures = this.filterProcedures(config, incentivizedProcedures);
      }
      this.cdr.detectChanges();
    });
  }

  private setProcedureData(
    config: PromotionalSmartshopperSettings,
    incentivizedProcedures: IncentivizedProcedure[]
  ): void {
    this.incentivizedProceduresCount = incentivizedProcedures.length;
    this.ssData = true;
  }

  private filterProcedures(
    config: PromotionalSmartshopperSettings,
    incentivizedProcedures: IncentivizedProcedure[]
  ): IncentivizedProcedure[] {
    let filterProcedures;
    if (config && config.procedure_ids && config.procedure_ids.length > 0) {
      const promotedProcedures = this.getIncentivizedProcedures(
        incentivizedProcedures,
        config.procedure_ids
      );
      filterProcedures =
        this.mergePromotedWithHighestIncentives(
          promotedProcedures,
          incentivizedProcedures
        ) || promotedProcedures;
    } else {
      filterProcedures = this.highestIncentivizedProcedures(
        incentivizedProcedures,
        this.maxPromotedProcedures
      );
    }
    return filterProcedures;
  }

  private mergePromotedWithHighestIncentives(
    promotedProcedures: IncentivizedProcedure[],
    incentivizedProcedures: IncentivizedProcedure[]
  ): IncentivizedProcedure[] | boolean {
    if (promotedProcedures.length < this.maxPromotedProcedures) {
      const highestIncentiveProcedures = this.highestIncentivizedProcedures(
        incentivizedProcedures,
        this.maxPromotedProcedures - promotedProcedures.length
      );
      return promotedProcedures.concat(highestIncentiveProcedures);
    }
    return false;
  }

  private getIncentivizedProcedures(
    incentivizedProcedures: IncentivizedProcedure[],
    procedureIds: number[]
  ) {
    const promotedProcedures = [];
    _.map(incentivizedProcedures, (procedure) => {
      if (_.includes(procedureIds, procedure.id)) {
        promotedProcedures.push(procedure);
      }
    });

    return promotedProcedures
      .sort((a, b) => {
        return procedureIds.indexOf(a.id) - procedureIds.indexOf(b.id);
      })
      .slice(0, this.maxPromotedProcedures);
  }

  private highestIncentivizedProcedures(
    incentivizedProcedures,
    procedureCount
  ) {
    let orderedProcedures = _.orderBy(
      incentivizedProcedures,
      (procedures) => procedures.high_incentive_amount,
      'desc'
    );
    orderedProcedures = orderedProcedures.slice(0, procedureCount);
    return orderedProcedures;
  }

  private setEarnIcon(): void {
    this.subscriptions.add(
      this.settingsService
        .getSetting('earn_icon_type')
        .pipe(filter((iconType) => !!iconType))
        .subscribe(
          (iconType) =>
            (this.earnIconSrc = `/assets/images/rts-earn-icon-${iconType}.svg`)
        )
    );
  }

  private setScheduleIcon(): void {
    this.subscriptions.add(
      this.settingsService
        .getSetting('schedule_icon_type')
        .pipe(filter((iconType) => !!iconType))
        .subscribe(
          (iconType) =>
            (this.scheduleIconSrc = `/assets/images/rts-schedule-icon-${iconType}.svg`)
        )
    );
  }

  private getPromotionalSsSettings(): Observable<PromotionalSmartshopperSettings> {
    return this.settingsService.getSetting('promotional_ss').pipe(first());
  }

  private subscribeToTranslationChange(): void {
    this.subscriptions.add(
      this.translateService.onLangChange.subscribe((event) => {
        this.setPromotedProcedures();
      })
    );
  }
}
